/**
 * Dashboard "ALGO TRADING" performance summary (2026-09-10 addition).
 *
 * Deliberately minimal, for novice users: This Month P&L, Profitable
 * Trades, Losing Trades, and a compact monthly P&L bar chart -- nothing
 * else (no Sharpe/drawdown/win-rate/profit-factor/strategy comparison,
 * per the feature's own explicit scope). Talks to the ONE new read-only
 * endpoint `GET /algo-performance/summary` (`positionService.
 * getAlgoPerformanceSummary`), which itself reuses the exact same
 * `AlgoPosition.realized_pnl` the rest of the app already shows -- no
 * second P&L engine here, just a display of numbers the backend already
 * computed.
 */
import { useEffect, useState } from "react";
import { Box, Card, CardContent, Grid, Stack, Typography, useTheme } from "@mui/material";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip as ChartTooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import positionService from "@/services/positionService";
import type { AlgoPerformanceSummaryRead } from "@/types";

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip);

function fmtSignedCurrency(value: number): string {
  const sign = value > 0 ? "+" : value < 0 ? "-" : "";
  return `${sign}₹${Math.abs(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtCompactCurrency(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 100000) return `${sign}₹${(abs / 100000).toFixed(1)}L`;
  if (abs >= 1000) return `${sign}₹${(abs / 1000).toFixed(1)}k`;
  return `${sign}₹${abs.toFixed(0)}`;
}

export function AlgoTradingSummaryCard() {
  const theme = useTheme();
  const [summary, setSummary] = useState<AlgoPerformanceSummaryRead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // No `months` argument -- lets the backend start the chart from this
    // user's first algo activation month instead of a fixed lookback.
    positionService
      .getAlgoPerformanceSummary()
      .then((result) => {
        if (!cancelled) setSummary(result);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Novice-friendly empty state: this user has never had a closed algo
  // trade at all (as opposed to just a quiet recent month) -- show one
  // clean message instead of a chart made entirely of zero-height bars.
  const hasAnyClosedTrades = Boolean(summary && summary.profitable_trades + summary.losing_trades > 0);

  const profitColor = theme.palette.success.main;
  const lossColor = theme.palette.error.main;

  const chartData = summary
    ? {
        labels: summary.monthly_pnl.map((m) => m.label),
        datasets: [
          {
            data: summary.monthly_pnl.map((m) => m.realized_pnl),
            backgroundColor: summary.monthly_pnl.map((m) => (m.realized_pnl >= 0 ? profitColor : lossColor)),
            borderRadius: 4,
            maxBarThickness: 36,
          },
        ],
      }
    : undefined;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: { raw: unknown }) => fmtSignedCurrency(Number(ctx.raw)),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: theme.palette.text.secondary },
      },
      y: {
        grid: { color: theme.palette.divider },
        ticks: {
          color: theme.palette.text.secondary,
          callback: (value: string | number) => fmtCompactCurrency(Number(value)),
        },
      },
    },
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          Algo Trading
        </Typography>

        {loading ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Loading algo performance…
          </Typography>
        ) : error || !summary ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Algo performance is temporarily unavailable.
          </Typography>
        ) : !hasAnyClosedTrades ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No completed algo trades yet — this section will fill in once your first algo position closes.
          </Typography>
        ) : (
          <Grid container spacing={3} alignItems="center" sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={4} md={3}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    This Month P&L ({summary.current_month_label})
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ color: summary.current_month_pnl >= 0 ? "success.main" : "error.main" }}
                  >
                    {fmtSignedCurrency(summary.current_month_pnl)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Profitable Trades
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="success.main">
                      {summary.profitable_trades}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Losing Trades
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color="error.main">
                      {summary.losing_trades}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={8} md={9}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Monthly Algo P&L
              </Typography>
              <Box sx={{ height: 180 }}>{chartData && <Bar data={chartData} options={chartOptions} />}</Box>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}

export default AlgoTradingSummaryCard;
